import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, LogOut, Menu, type LucideIcon } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { cn } from "@/lib/utils";

// 端侧共享外壳(主题迁移自 key-health-check 前端,2026-09-17):
// 深藏青侧栏 #101827 + 白色 68px 顶栏 + 面包屑 + 右下 `服务正常`/账号块;
// 移动端(< md)侧栏抽屉化 + scrim。AGPLv3 归属版权由 footer 承载,
// 运营端 / 供应商端分别传入自己的导航与页脚。

type ShellLeaf = { to: string; label: string; icon: LucideIcon; prefix?: boolean };
type ShellNavItem =
  | { type: "leaf"; item: ShellLeaf }
  | { type: "group"; label: string; icon: LucideIcon; prefix?: string; children: ShellLeaf[] };

type ShellSection = { label?: string; items: ShellNavItem[] };

interface ShellProps {
  brandMark: React.ReactNode;
  brandLabel: string;
  brandSub: string;
  sections: ShellSection[];
  accountName: string;
  accountSub: string;
  onLogout: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

function isActivePath(leaf: ShellLeaf, pathname: string): boolean {
  return leaf.prefix ? pathname.startsWith(leaf.to) : pathname === leaf.to;
}

export function ShellScaffold({
  brandMark,
  brandLabel,
  brandSub,
  sections,
  accountName,
  accountSub,
  onLogout,
  footer,
  children,
}: ShellProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);

  let crumb = "";
  outer: for (const section of sections) {
    for (const item of section.items) {
      if (item.type === "leaf" && isActivePath(item.item, pathname)) {
        crumb = item.item.label;
        break outer;
      }
      if (item.type === "group") {
        for (const child of item.children) {
          if (isActivePath(child, pathname)) {
            crumb = `${item.label} / ${child.label}`;
            break outer;
          }
        }
      }
    }
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {mobileOpen && (
          <button
            className="fixed inset-0 z-20 bg-[#07102266] md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="关闭菜单"
          />
        )}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-30 flex w-60 shrink-0 flex-col bg-navy transition-transform duration-200",
            "md:static md:z-auto md:translate-x-0",
            mobileOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="px-[15px] pb-9 pt-6">
            <div className="flex items-center gap-3 px-1">
              {brandMark}
              <div className="min-w-0">
                <strong className="block truncate text-[15px] tracking-[.01em] text-[#f8fbff]">{brandLabel}</strong>
                <span className="mt-0.5 block truncate text-[10px] tracking-[.1em] text-[#71819e]">{brandSub}</span>
              </div>
            </div>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto px-[15px] pb-2">
            {sections.map((section, i) => (
              <div key={section.label ?? `sec-${i}`} className="mb-1">
                {section.label && (
                  <div className="px-3 pb-2 pt-4 text-[10px] font-extrabold uppercase tracking-[.15em] text-[#64748e]">
                    {section.label}
                  </div>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    if (item.type === "leaf") {
                      const active = isActivePath(item.item, pathname);
                      const Icon = item.item.icon;
                      return (
                        <Link
                          key={item.item.to}
                          to={item.item.to}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-2.5 rounded-md px-3 py-2.5 text-xs font-bold text-[#aab8cf] transition-colors",
                            active
                              ? "bg-navy-active text-white shadow-[inset_3px_0_0] shadow-accentbar"
                              : "hover:bg-navy-hover hover:text-white",
                          )}
                        >
                          <Icon size={17} />
                          {item.item.label}
                          {active && <ChevronRight size={14} className="ml-auto text-[#82a9ff]" />}
                        </Link>
                      );
                    }
                    const GroupIcon = item.icon;
                    const groupActive = item.prefix ? pathname.startsWith(item.prefix) : false;
                    return (
                      <div key={item.label}>
                        <div
                          className={cn(
                            "flex items-center gap-2.5 rounded-md px-3 py-2.5 text-xs font-bold transition-colors",
                            groupActive ? "text-white" : "text-[#7e91af]",
                          )}
                        >
                          <GroupIcon size={17} />
                          {item.label}
                        </div>
                        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-[#243149] pl-3">
                          {item.children.map((child) => {
                            const active = isActivePath(child, pathname);
                            const Icon = child.icon;
                            return (
                              <Link
                                key={child.to}
                                to={child.to}
                                onClick={() => setMobileOpen(false)}
                                className={cn(
                                  "flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold transition-colors",
                                  active
                                    ? "bg-navy-active text-white shadow-[inset_3px_0_0] shadow-accentbar"
                                    : "text-[#98a8c2] hover:bg-navy-hover hover:text-white",
                                )}
                              >
                                <Icon size={15} />
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          <div className="grid gap-4 px-[15px] pb-4">
            <div className="flex items-center gap-2 px-3 text-[11px] text-[#7e91af]">
              <i className="h-[7px] w-[7px] rounded-full bg-[#37c77d] shadow-[0_0_0_4px_#37c77d24]" />
              服务正常
            </div>
            <button
              className="flex items-center gap-2.5 border-t border-[#243149] px-3 pb-1 pt-4 text-left"
              onClick={onLogout}
              title="退出登录"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#294b87] text-[11px] font-extrabold text-[#dce8ff]">
                {accountName.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <strong className="block truncate text-[11px] font-semibold text-[#f1f5fb]">{accountName}</strong>
                <small className="block truncate text-[10px] text-[#71819c]">{accountSub}</small>
              </span>
              <LogOut size={15} className="shrink-0 text-[#71819c]" />
            </button>
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex h-[68px] shrink-0 items-center gap-3 border-b bg-white px-5 lg:px-8">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="打开菜单">
              <Menu size={20} className="text-[#172238]" />
            </button>
            <div className="truncate text-xs font-semibold text-[#7d899d]">
              {brandLabel}
              <span className="mx-2 text-[#c1cbd8]">/</span>
              {crumb || "—"}
            </div>
            <div className="ml-auto flex items-center gap-4 lg:gap-5">
              <span className="text-[11px] text-[#8b96a6]">{new Date().toLocaleDateString("zh-CN")}</span>
              <LanguageSwitcher />
            </div>
          </header>
          <main key={pathname} className="min-h-0 flex-1 overflow-auto">
            <div className="mx-auto w-full max-w-[1440px] px-6 py-8 lg:px-10">{children}</div>
          </main>
        </div>
      </div>
      {footer && (
        <footer className="shrink-0 border-t bg-white px-4 py-2 text-center text-xs text-muted-foreground">{footer}</footer>
      )}
    </div>
  );
}

export type { ShellLeaf, ShellNavItem, ShellSection };